/**
 * The Timeline canvas's corner toolbar — one horizontal bar holding the draw-line actions,
 * each in its own outlined circle, followed by the Sync button.
 *
 * Replaces the draggable `CircleMenu` that previously held the two line actions while Sync sat
 * beside it as a separate floating pill: two pieces of chrome in the same corner, one of which
 * could be dragged out from under the other. One fixed bar instead.
 *
 * Positioned (by CSS) at the canvas's upper-right corner, deliberately OVERLAPPING the frozen
 * row strip rather than sitting below it. It is not part of that strip: GridCanvas renders its
 * `overlay` slot last, inside the canvas wrapper but outside every pan/zoom-transformed pane, so
 * this bar neither scrolls with the frozen row nor participates in its clipping — it simply
 * paints on top of whatever the corner happens to contain.
 *
 * Presentational, in CircleMenu's "caller owns what each item does" spirit: PlanPage supplies the
 * actions and what trails them.
 */
import React from 'react';

export interface PlanToolbarAction {
    id: string;
    /** Icon-only content — no visible text label is rendered. */
    icon: React.ReactNode;
    /** Accessible name for the button, since there is no visible text to read instead. */
    ariaLabel: string;
    /** Renders the action as currently engaged (e.g. a line-placement mode awaiting a click). */
    active?: boolean;
    onClick: () => void;
}

interface PlanTimelineToolbarProps {
    actions: PlanToolbarAction[];
    /** Trailing content — the Sync button. */
    children?: React.ReactNode;
}

export default function PlanTimelineToolbar({ actions, children }: PlanTimelineToolbarProps) {
    return (
        <div className='plan-timeline-toolbar'>
            {actions.map((action) => (
                <button
                    key={action.id}
                    type='button'
                    className='plan-timeline-toolbar__btn'
                    aria-label={action.ariaLabel}
                    aria-pressed={action.active ?? undefined}
                    title={action.ariaLabel}
                    onClick={action.onClick}
                >
                    {action.icon}
                </button>
            ))}
            {/* No divider before the trailing content: each icon action carries its own outlined
                circle (see .plan-timeline-toolbar__btn), which distinguishes them from the
                labelled Sync button on its own. A rule between the two read as a grouping
                boundary that does not actually mean anything — these are all just canvas
                actions. */}
            {children}
        </div>
    );
}
